import { createStore } from "vuex";
import config from "@/config";
import auth from "./auth"; // Ensure auth module is imported
import { getCountries } from "@/api";
import { showAlertModal } from "@/utils";
import { i18n } from "@/i18n";

export default createStore({
    state: {
        mini_sidebar: config.mini_sidebar,
        actTheme: config.actTheme,
        fontTheme: config.fontTheme,
        isMobile: false,
        submitRequested: false,
        countries: [],
        selectedLanguage: "en",
        formButtons: [],
        alert: {
            show: false,
            message: "",
            type: "",
            duration: null,
        },
        alertQueue: [],
        alertTimeoutId: null,
    },
    getters: {},
    mutations: {
        SET_ALERT(state, { message, type, duration, visible, errorDetails }) {
            state.alert.message = message;
            state.alert.type = type;
            state.alert.duration = duration;
            state.alert.show = visible;
            state.alert.errorDetails = errorDetails || "";
            state.alert.remainingDuration = duration;
        },
        clearCurrentAlert(state) {
            state.alert.show = false;
            state.alert.message = "";
            state.alert.type = "";
            state.alert.duration = null;
            state.alert.remainingDuration = null;
            state.alert.errorDetails = "";

            if (state.alertTimeoutId) {
                clearTimeout(state.alertTimeoutId);
                state.alertTimeoutId = null;
            }
        },
        queueAlert(state, alert) {
            state.alertQueue.push(alert);
        },
        SET_FORM_BUTTONS(state, buttons) {
            state.formButtons = buttons;
        },
        RESET_FORM_BUTTONS(state) {
            state.formButtons = [];
        },
        SET_MINI_SIDEBAR(state, payload) {
            state.mini_sidebar = payload;
        },
        SET_THEME(state, payload) {
            state.actTheme = payload;
        },
        SET_FONT(state, payload) {
            state.fontTheme = payload;
        },
        SET_IS_MOBILE(state, value) {
            state.isMobile = value;
        },
        REQUEST_SUBMIT(state) {
            state.submitRequested = true;
        },
        RESET_SUBMIT(state) {
            state.submitRequested = false;
        },
        SET_COUNTRIES(state, countries) {
            state.countries = countries;
        },
    },
    actions: {
        configureAlert({ commit, state, dispatch }, alert) {
            if (state.alertTimeoutId) {
                clearTimeout(state.alertTimeoutId);
                state.alertTimeoutId = null;
            }

            commit("SET_ALERT", alert);

            state.alertTimeoutId = setTimeout(() => {
                commit("clearCurrentAlert");
                dispatch("processNextAlert");
            }, alert.duration);
        },
        processNextAlert({ state, dispatch }) {
            if (state.alertQueue.length > 0) {
                const nextAlert = state.alertQueue.shift();
                if (nextAlert) {
                    if (nextAlert.type !== "danger" && !nextAlert.visible) {
                        nextAlert.visible = true;
                    }
                    dispatch("configureAlert", nextAlert);
                }
            }
        },
        showAlertModal(
            { commit, dispatch, state },
            { message, type, duration, errorDetails }
        ) {
            if (type === "danger") {
                commit("clearCurrentAlert");
                dispatch("configureAlert", {
                    message,
                    type,
                    duration,
                    visible: true,
                    errorDetails,
                });
            } else {
                const currentAlertType = state.alert.type;
                if (currentAlertType === "danger" && state.alert.show) {
                    commit("queueAlert", {
                        message,
                        type,
                        duration,
                        errorDetails,
                    });
                } else {
                    dispatch("configureAlert", {
                        message,
                        type,
                        duration,
                        visible: true,
                        errorDetails,
                    });
                }
            }
        },
        setMiniSidebar({ commit }, payload) {
            commit("SET_MINI_SIDEBAR", payload);
        },
        setTheme({ commit }, payload) {
            commit("SET_THEME", payload);
        },
        setFont({ commit }, payload) {
            commit("SET_FONT", payload);
        },
        updateScreenSize({ commit, state }) {
            // Keep in sync with styles.scss @media (max-width: 1145px) for sidebar layout
            const isMobile = window.matchMedia("(max-width: 1145px)").matches;
            const wasMobile = state.isMobile;
            commit("SET_IS_MOBILE", isMobile);
            // Small screens: default to icon rail; desktop: full-width drawer
            if (isMobile && !wasMobile) {
                commit("SET_MINI_SIDEBAR", true);
            }
            if (!isMobile && wasMobile) {
                commit("SET_MINI_SIDEBAR", false);
            }
        },
        async fetchCountries({ commit, state }) {
            const lang = state.selectedLanguage;
            try {
                const countries = await getCountries(lang);
                commit("SET_COUNTRIES", countries);
            } catch (error) {
                console.error("Error fetching countries:", error);
                showAlertModal(
                    this.$store,
                    i18n.global.t("common.fetchCountriesFailed"),
                    "danger",
                    12000,
                    error
                );
            }
        },
    },

    modules: {
        auth, // Register auth module properly
    },
});
